/**
@license
Copyright (c) 2015 The Polymer Project Authors. All rights reserved.
This code may only be used under the BSD style license found at http://polymer.github.io/LICENSE.txt
The complete set of authors may be found at http://polymer.github.io/AUTHORS.txt
The complete set of contributors may be found at http://polymer.github.io/CONTRIBUTORS.txt
Code distributed by Google as part of the polymer project is also
subject to an additional IP rights grant found at http://polymer.github.io/PATENTS.txt
*/
import '@polymer/polymer/polymer-legacy.js';

//import 'oidc-client/dist/oidc-client.js';
import OidcClientImports from 'oidc-client/index.js';
const UserManager = OidcClientImports.UserManager;
import { PolymerElement } from '@polymer/polymer/polymer-element.js';

/**
 * @constructor
 * @param {Promise} promise
 */
function DefaultPromise(promise) {

   /**
    * @param {function(*):*} successCallback
    * @param {function(*):*} errorCallback
    * @returns {DefaultPromise}
    */
    this.then = function (successCallback, errorCallback) {
        var childPromise = promise.then(successCallback, errorCallback);

        return new DefaultPromise(childPromise);
    };

   /**
    *
    * @param {function(*):*} errorCallback
    * @returns {DefaultPromise}
    */
    this.catch = function (errorCallback) {
        var childPromise = promise.catch(errorCallback);

        return new DefaultPromise(childPromise);
    };
}

function DefaultPromiseFactory() {

    this.resolve = function (value) {
        return new DefaultPromise(Promise.resolve(value));
    };

    this.reject = function (reason) {
        return new DefaultPromise(Promise.reject(reason));
    };

   /**
    * @param {function(resolve:function, reject:function)} callback
    * @returns {DefaultPromise}
    */
    this.create = function (callback) {
        return new DefaultPromise(new Promise(callback));
    };
}

function Token(other) {
    if (other) {
        this.profile = other.profile;
        this.id_token = other.id_token;
        this.access_token = other.access_token;
        if (other.access_token) {
            this.expires_at = parseInt(other.expires_at);
        }
        else if (other.id_token) {
            this.expires_at = other.profile.exp;
        }
        else {
            throw Error("Either access_token or id_token required.");
        }
        this.scope = other.scope;
        this.session_state = other.session_state;
    }
    else {
        this.expires_at = 0;
    }

    Object.defineProperty(this, "scopes", {
        get: function () {
            return (this.scope || "").split(" ");
        }
    });

    Object.defineProperty(this, "expired", {
        get: function () {
            var now = parseInt(Date.now() / 1000);
            return this.expires_at < now;
        }
    });

    Object.defineProperty(this, "expires_in", {
        get: function () {
            var now = parseInt(Date.now() / 1000);
            return this.expires_at - now;
        }
    });
}

Token.fromResponse = function (response) {
    if (response.access_token) {
        var now = parseInt(Date.now() / 1000);
        response.expires_at = now + parseInt(response.expires_in);
    }
    return new Token(response);
}

Token.fromJSON = function (json) {
    if (json) {
        try {
            var obj = JSON.parse(json);
            return new Token(obj);
        }
        catch (e) {
        }
    }
    return new Token(null);
}

Token.prototype.toJSON = function () {
    return JSON.stringify({
        profile: this.profile,
        id_token: this.id_token,
        access_token: this.access_token,
        expires_at: this.expires_at,
        scope: this.scopes.join(" "),
        session_state: this.session_state
    });
}

function copy(obj, target) {
    target = target || {};
    for (var key in obj) {
        if (obj.hasOwnProperty(key)) {
            target[key] = obj[key];
        }
    }
    return target;
}

var _promiseFactory = new DefaultPromiseFactory();

var TokenManager = {
    _userManager: null,
    _settings: {
        persist: true,
        store: window.localStorage,
        filterProtocolClaims: false,
        loadUserInfo: false,
        persistKey: "tcg-signin-aware.token",
        _silent_renew: false,
        response_type: "id_token token",
        set silent_renew(val) {
            this._silent_renew = val;
        },
        get silent_renew() {
            return this._silent_renew;
        },
        _silent_init: false,
        set silent_init(val) {
            this._silent_init = val;
        },
        get silent_init() {
            return this._silent_init;
        },
        _redirect_uri: null,
        set redirect_uri(val) {
            this._redirect_uri = val;
        },
        get redirect_uri() {
            return this._computeUri(this._redirect_uri, this.proxy_uri);
        },
        _silent_redirect_uri: null,
        set silent_redirect_uri(val) {
            this._silent_redirect_uri = val;
        },
        get silent_redirect_uri() {
            return this._computeUri(this._silent_redirect_uri, this.redirect_uri);
        },
        _proxy_uri: null,
        set proxy_uri(val) {
            this._proxy_uri = val;
        },
        get proxy_uri() {
            return this._computeUri(this._proxy_uri);
        },
        _post_logout_redirect_uri: null,
        set post_logout_redirect_uri(val) {
            this._post_logout_redirect_uri = val;
        },
        get post_logout_redirect_uri() {
            return this._computeUri(this._post_logout_redirect_uri);
        },
        _computeUri: function(val, fallback) {
            if (!val) {
                return fallback || [location.protocol, '//', location.host, location.pathname].join('');
            }
            else if (val.indexOf('http') === 0) {
            }
            else if (val.indexOf('/') === 0) {
                return [location.protocol, '//', location.host, val].join('');
            }
            var pathname = location.pathname.substring(0, location.pathname.lastIndexOf('/')) + '/' + val;
            return [location.protocol, '//', location.host, pathname].join('');
        }
    },
    _token: null,
    _signinAwares: [],
    _signedIn: false,
    _callbacks: {
        tokenObtainedCallbacks: [],
        tokenExpiringCallbacks: [],
        tokenExpiredCallbacks: [],
        tokenRemovedCallbacks: [],
        tokenRenewFailedCallbacks: [],
        tokenSilentInitFailedCallbacks: []
    },
    
    get signedIn() {
        if (this._token) {
            return !this._token.expired;
        }
        return false;
    },
    get token() {
        if (this._token && !this._token.expired) {
            return this._token;
        }
        return null;
    },
    get expired() {
        if (this._token) {
            return this._token.expired;
        }
        return true;
    },
    get expires_in() {
        if (this._token) {
            return this._token.expires_in;
        }
        return 0;
    },
    
    set authority(val) {
        if (val && val.indexOf('.well-known/openid-configuration') < 0) {
            if (val[val] !== '/') {
                val += '/';
            }
            val += '.well-known/openid-configuration';
        }
        this._settings.authority = val;
    },
    set clientId(val) {
        this._settings.client_id = val;
    },
    set scope(val) {
        this._settings.scope = val;
    },
    set callbackUri(val) {
        this._settings.redirect_uri = val;
    },
    set proxyUri(val) {
        this._settings.proxy_uri = val;
    },
    set postLogoutRedirectUri(val) {
        this._settings.post_logout_redirect_uri = val;
    },
    set silentRenew(val) {
        // silentRenew is false by default an can only be changed to true.
        if (val === true) { 
            this._settings.silent_renew = true;
        }
    },
		set silentInit(val) {
        // silentInit is false by default an can only be changed to true.
        if (val === true) { 
            this._settings.silent_init = true;
        }
    },
    set persist(val) {
        // persist is true by default an can only be changed to false.
        if (val === false) { 
            this._settings.persist = false;
        }
    },
    set acrValues(val) {
        this._settings.acr_values = val;
    },
    set useSameWindow(val) {
        this._settings._useSameWindow = val;
    },
    
    _initialize: function() {
        this._initialize = function() {};
                    
        var self = this;

        this._userManager = new UserManager(this._settings);
        this._loadToken();

        if (self._settings._useSameWindow && !self.token && window.location.hash && window.location.hash.match(/id_token=/)) {
            self._processHash(window);
        }
        
        if (this._settings.silent_init)
        {
            self._initTokenSilentAsync()
                .catch(function(e) {
                        self._callTokenSilentInitFailed();
                        console.error(e.message || e);
                    });
        }
        
        window.setTimeout(function() {
            self._configureAutoRenewToken();
            window.setTimeout(function() {
                self._configureTokenExpiring();
                self._configureTokenExpired();
            }, 0);
        }, 0);
    },
    _configureTokenExpiring: function() {
        var self = this,
            handle = null;
        
        function callback() {
            handle = null;
            self._callTokenExpiring();
        }
        
        function cancel() {
            if (handle) {
                window.clearTimeout(handle);
                handle = null;
            }
        }
        
        function setup(duration) {
            handle = window.setTimeout(callback, duration * 1000);
        }
        
        function configure() {
            cancel();

            if (!self.expired) {
                var duration = self.expires_in;
                if (duration > 60) {
                    setup(duration - 60);
                } else {
                    callback();
                }
            }
        }
        
        configure();
        
        this._addOnTokenObtained(configure);
        this._addOnTokenRemoved(cancel);
    },
    _configureTokenExpired: function() {
        var self = this,
            handle = null;
        
        function callback() {
            handle = null;
            self._removeToken();
            self._callTokenExpired();
        }
        
        function cancel() {
            if (handle) {
                window.clearTimeout(handle);
                handle = null;
            }
        }
        
        function setup(duration) {
            handle = window.setTimeout(callback, duration * 1000);
        }
        
        function configure() {
            cancel();
            if (self.expires_in > 0) {
                // register 1 second beyond expiration to prevent edge conditions for expiration
                setup(self.expires_in + 1);
            }
        }
        
        configure();
        
        this._addOnTokenObtained(configure);
        this._addOnTokenRemoved(cancel);
        
    },
    _configureAutoRenewToken: function() {
        var self = this;

        if (this._settings.silent_renew) {
            this._addOnTokenExpiring(function() {
                self._renewTokenSilentAsync()
                    .catch(function(e) {
                        self._callTokenRenewFailed();
                        console.error(e.message || e);
                    });
            });
        }
    },
    
    _addOnTokenObtained: function(cb) {
        this._callbacks.tokenObtainedCallbacks.push(cb);
    },
    _addOnTokenExpiring: function(cb) {
        this._callbacks.tokenExpiringCallbacks.push(cb);
    },
    _addOnTokenExpired: function(cb) {
        this._callbacks.tokenExpiredCallbacks.push(cb);
    },
    _addOnTokenRemoved: function(cb) {
        this._callbacks.tokenRemovedCallbacks.push(cb);
    },
    _addOnTokenRenewFailed: function(cb) {
        this._callbacks.tokenRenewFailedCallbacks.push(cb);
    },
		_addOnTokenSilentInitFailed: function(cb) {
        this._callbacks.tokenSilentInitFailedCallbacks.push(cb);
    },
    
    _callTokenObtained: function() {
        var self = this;
            
        this._callbacks.tokenObtainedCallbacks.forEach(function(cb) {
            cb();
        });
        this._signinAwares.forEach(function(aware) {
            self._updateAware(aware);
            aware._onTokenEvent('token-obtained');
        });
    },
    _callTokenExpiring: function() {
        this._callbacks.tokenExpiringCallbacks.forEach(function(cb) {
            cb();
        });
        this._signinAwares.forEach(function(aware) {
            aware._onTokenEvent('token-expiring');
        });
    },
    _callTokenExpired: function() {
        this._callbacks.tokenExpiredCallbacks.forEach(function(cb) {
            cb();
        });
        this._signinAwares.forEach(function(aware) {
            aware._onTokenEvent('token-expired');
        });
    },
    _callTokenRenewFailed: function() {
        this._callbacks.tokenRenewFailedCallbacks.forEach(function(cb) {
            cb();
        });
        this._signinAwares.forEach(function(aware) {
            aware._onTokenEvent('token-renew-failed');
        });
    },
		_callTokenSilentInitFailed: function() {
        this._callbacks.tokenSilentInitFailedCallbacks.forEach(function(cb) {
            cb();
        });
        this._signinAwares.forEach(function(aware) {
            aware._onTokenEvent('token-silent-init-failed');
        });
    },
    _callTokenRemoved: function() {
        var self = this;
        this._callbacks.tokenRemovedCallbacks.forEach(function(cb) {
            cb();
        });
        this._signinAwares.forEach(function(aware) {
            self._updateAware(aware);
            aware._onTokenEvent('token-removed');
        });
    },
    
    _loadToken: function() {
        var token = null;
        if (this._settings.persist) {
            var tokenJson = this._settings.store.getItem(this._settings.persistKey);
            if (tokenJson) {
                token = Token.fromJSON(tokenJson);
                if (token.expired) {
                    token = null;
                }
            }
            this._token = token;
        }
        return token;
    },
    _saveToken: function(token) {
        if (token && !(token instanceof Token)) {
            token = Token.fromResponse(token);
        }
        
        this._token = token;
        if (this._settings.persist && !this.expired) {
            this._settings.store.setItem(this._settings.persistKey, token.toJSON());
        } else {
            this._settings.store.removeItem(this._settings.persistKey);
        }
        
        if (token) {
            this._callTokenObtained();
        } else {
            this._callTokenRemoved();
        }
        
        this._updateAwares();
    },
    _removeToken: function() {
        if (this._token) {
            this._saveToken(null);
        }
    },
    _updateAwares: function() {
        var self = this;
        this._signinAwares.forEach(function(aware) {
            self._updateAware(aware);
        });
    },
    _updateAware: function(aware) {
        aware._setSignedIn(this.signedIn);
        aware._setToken(this.token);
    },
    _initTokenSilentAsync: function() {
        return this._userManager.createSigninRequest({prompt: "none"})
            .then(signInRequest => this._initTokenSilentInIFrameAsync(signInRequest.url))
            .then(url => this._oidcClient.processSigninResponse(url))
            .then(token => {
                this._saveToken(token);
            });
    },
    _initTokenSilentInIFrameAsync: function(url) {
        if (!url) {
            return _promiseFactory.reject("No url provided");
        }

        return _promiseFactory.create(function(resolve, reject) {
            var frame = window.document.createElement("iframe");
            frame.style.display = "none";
            window.document.body.appendChild(frame);
            frame.src = url;
            
            function resolveHashAndRemoveFrame(){
                try {
                    var hash = frame.contentDocument.location.hash;
                    resolve(hash);
                }
                finally {
                    window.document.body.removeChild(frame);
                }
            }
            
             window.setTimeout(function(){
                 reject("No token obtained within timeout");
             }, 5000);
            
            frame.addEventListener("load", function() {
                resolveHashAndRemoveFrame();
            });
        });
    },
    _renewTokenSilentAsync: function() {
        return this._userManager.createSigninRequest({prompt: "none"})
            .then(signInRequest => this._initTokenSilentInIFrameAsync(signInRequest.url))
            .then(url => this._userManager.processSigninResponse(url))
            .then(token => {
                this._saveToken(token);
            });
    },

    _processHash: function(wnd) {
        return this._userManager.processSigninResponse(wnd.location.hash).then(
            token => {
                this._saveToken(token);

                let url = wnd.location.pathname + wnd.location.search;
                if(token.state && token.state.url) {
                    url = token.state.url;
                }
                // Redirect to calling url...
                history.pushState("", wnd.document.title, url);
            },
            error => {
                console.error("Problem getting token : " + (error.message || error));
                history.pushState("", wnd.document.title, wnd.location.pathname + wnd.location.search);
            }
        );
    },
    
    attachSigninAware: function(aware) {
        this._initialize();

        if (this._signinAwares.indexOf(aware) === -1) {
            this._signinAwares.push(aware);
            this._updateAware(aware);
        } else {
            console.warn('signinAware attached more than once', aware);
        }
    },
    detachSigninAware: function(aware) {
        var index = this._signinAwares.indexOf(aware);
        if (index !== -1) {
            this._signinAwares.splice(index, 1);
        } else {
            console.warn('Trying to detach unattached signin-aware');
        }
    },
    
    signIn: function() {
        
        if (this._wnd) {
            this._wnd.focus();
            return;
        }
        
        var self = this;
        
        var uri = this._settings.proxy_uri || "about:blank";
        
        if(!this._settings._useSameWindow) {
            const wWidth = 650;
            const wHeight = 600;
            const wTop = window.screenY + (window.outerHeight / 2 - wHeight / 2);
            const wLeft = window.screenX + (window.outerWidth / 2 - wWidth / 2);

            this._wnd = window.open(uri, "_blank", "height=" + wHeight + ",width=" + wWidth + ",left=" + wLeft + ",top=" + wTop + ",toolbar=0,scrollbars=0,status=0,resizable=1,location=0,menuBar=0");
        }

        this._userManager.createSigninRequest({
            acr_values: this._settings.acr_values,
            state: { url: window.location.pathname + window.location.search }
        })
        .then(request => {
            if (self._wnd) {
                self._wnd.window.location = request.url;
            } else if (self._settings._useSameWindow) {
                window.location.replace(request.url);
            }
        }, error => {
            console.error(error && error.message || error);
            if (self._wnd) {
                self._wnd.close();
            }
        });
        
        if (this._wnd) {
            this._wnd.focus();
            
            var interval = window.setInterval(() => {
                if (self._wnd.closed) {
                    clearInterval(interval);
                    self._wnd = null;
                } else {
                    try {
                        var loc = self._wnd.location.href;
                        if (typeof loc !== "undefined" && loc.indexOf(self._settings.redirect_uri) === 0 && self._wnd.location.hash) {
                            self._processHash(self._wnd);
                            self._wnd.close();
                        }
                    }
                    catch (e) { /* security exceptions are expected in this case, so ignore them. */ }
                }
            }, 250);
        }
    },
    signOut: function() {
        
        this._userManager.signoutRedirect().then(
            request => {
                this._removeToken();
                top.window.location = request.url;
            },
            error => {
                console.error("TokenManager.signOut error: " + (error && error.message || error || ""));
            }
        );
    }
};

/**
`tcg-signin-aware` is used to enable authentication in custom elements.
        
You can bind to `signedIn` property to monitor signed in state.

You can bind to `token` property to receive the token. 
        
##### Example bind to signedIn and token property

    <tcg-signin-aware
        signed-in="{{signedIn}}"
        token="{{token}}">
    </tcg-signin-aware>

##### Example events
        
        <template id="awareness" is="dom-bind">
            <tcg-signin-aware
                on-token-obtained="handleTokenObtained"
                on-token-removed="handleTokenRemoved">
            </tcg-signin-aware>
        <\/template>
        <script>
            var aware = document.querySelector('#awareness');
            aware.handleTokenObtained = function() {
                console.log('Token obtained.');
            };
            aware.handleTokenRemoved = function() {
                console.log('Token removed.');
            };
        <\/script>

*/
class TcgSigninAware extends PolymerElement {
    static get is() { return 'tcg-signin-aware'; }
     /**
    * Fired when a new token is obtained.
    * @event token-obtained
    */
    /**
    * Fired when the token is about to expire.
    * @event token-expiring
    */
    /**
    * Fired when token renewal fails.
    * @event token-renew-failed
    */
    /**
    * Fired when the token has expired.
    * @event token-expired
    */
    /**
    * Fired when the token is removed from scope (and storage).
    * @event token-removed
    */
    static get properties() {
        return {
        /**
        * The url of the security token service to use.
        */
        authority: {
            type: String,
            observer: '_authorityChanged'
        },
        /**
        * The unique ID of the client (application) that is registered in the identityserver.
        */
        clientId: {
            type: String,
            observer: '_clientIdChanged'
        },
        /**
        * The (space-delimited) scopes the to provide access to.
        */
        scope: {
            type: String,
            observer: '_scopeChanged'
        },
        /**
        * The uri to redirect to after signin in. This is the page the tokens are returned to in the hash fragment.
        * When not set...
        *   - and proxyUri is set, the (computed) value of proxyUri is used.
        *   - otherwise the current uri is used.
        * 
        * When set...
        *   - and the value starts with http(s), the value is returned as-is.
        *   - else if the value starts with a / the uri is computed using the website root + the value.
        *   - otherwise the uri is computed using the level of the current uri + the value.
        */
        callbackUri: {
            type: String,
            observer: '_callbackUriChanged'
        },
        /**
        * The uri to redirect to after signin out.
        *
        * When not set the current uri is used.
        *
        * When set,
        *   - and the value starts with http(s), the value is returned as-is.
        *   - else if the value starts with a / the uri is computed using the website root + the value.
        *   - otherwise the uri is computed using the level of the current uri + the value.
        */
        postLogoutRedirectUri: {
            type: String,
            observer: '_postLogoutRedirectUriChanged'
        },
        /**
        * The initial page to load when loading the signin page.
        *
        * When not set "about:blank" is used, which results in a blank page untill the login page is loaded.
        *
        * When set...
        *   - and the value starts with http(s), the value is returned as-is.
        *   - else if the value starts with a / the uri is computed using the website root + the value.
        *   - otherwise the uri is computed using the level of the current uri + the value.
        */
        proxyUri: {
            type: String,
            observer: '_proxyUriChanged'
        },
        /**
        * True will force an automatic renewal of the token (silently) when it is about to expire.
        */
        silentRenew: {
            type: Boolean,
            value: false,
            observer: '_silentRenewChanged'
        },
        /**
        * True will force an automatic token fetch (silently) on initialization
        */
        silentInit: {
            type: Boolean,
            value: false,
            observer: '_silentInitChanged'
        },
        /**
        * True will prevent token persistence.
        */
        noPersistence: {
            type: Boolean,
            value: false,
            observer: '_noPersistenceChanged'
        },
        /**
         * Corresponds to the acr_values setting in OpenID Connect
         */
        acrValues : {
            type: String,
            observer: '_acrValuesChanged'
        },
        /**
        * Set this to use same window. Needed for Progressive Web Apps
        */
        useSameWindow: {
            type: Boolean,
            observer: '_useSameWindowChanged'
        },
        /**
        * True if user is signed in.
        */
        signedIn: {
            type: Boolean,
            notify: true,
            readOnly: true
        },
        /**
        * The token object containing the requested scope data (e.g id_token, access_token, profile).
        */
        token: {
            type: Object,
            notify: true,
            readOnly: true
        }
    };
    }

    // Element Lifecycle
    connectedCallback () {
        super.connectedCallback();

        TokenManager.attachSigninAware(this);
    }
    disconnectedCallback () {
        super.disconnectedCallback();

        TokenManager.detachSigninAware(this);
    }

    // Element Behavior
    _authorityChanged(newVal, oldVal) {
        TokenManager.authority = newVal;
    }
    _clientIdChanged(newId, oldId) {
        TokenManager.clientId = newId;
    }
    _scopeChanged(newVal, oldVal) {
        TokenManager.scope = newVal;
    }
    _callbackUriChanged(newUri, oldUri) {
        TokenManager.callbackUri = newUri;
    }
    _postLogoutRedirectUriChanged(newUri, oldUri) {
        TokenManager.postLogoutRedirectUri = newUri;
    }
    _proxyUriChanged(newUri, oldUri) {
        TokenManager.proxyUri = newUri;
    }
    _silentRenewChanged(newVal, oldVal) {
        TokenManager.silentRenew = newVal;
    }
		_silentInitChanged(newVal, oldVal) {
        TokenManager.silentInit = newVal;
    }
    _noPersistenceChanged(newVal, oldVal) {
        TokenManager.persist = !newVal;
    }
    _acrValuesChanged(newVal, oldVal) {
        TokenManager.acrValues = newVal;
    }
    _useSameWindowChanged(newVal, oldVal) {
        TokenManager.useSameWindow = newVal;
    }
            
    _onTokenEvent(event) {
        this.dispatchEvent(new CustomEvent(event, { bubbles: true }));
    }
    
    /**
    * Sign in user. Opens the authorization dialog for signing in.
    */
    signIn() {
        TokenManager.signIn();
    }
    /**
    * Sign out user.
    */
    signOut() {
        TokenManager.signOut();
    }

    saveToken(token) {
        TokenManager._saveToken(new Token(token));
    }
}

window.customElements.define(TcgSigninAware.is, TcgSigninAware);
